import React from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronLeft, faClose } from '@fortawesome/free-solid-svg-icons'

import classNames from 'classnames'
import classes from './styles.module.scss'

/** Close affordance: `true`/`'back'` = top-left back chevron (default),
 *  `'close'` = top-right ✕ button. */
export type ModalCloseIcon = boolean | 'back' | 'close'

export interface ModalProps {
  isVisible: boolean
  onClose?: () => void
  children?: React.ReactNode,
  size?: string
  closeIcon?: ModalCloseIcon
  /** Vertical placement of the modal in the viewport. Defaults to 'center'. */
  placement?: 'center' | 'top'
}

export interface ModalIconProps {
  children: React.ReactNode
  className?: string
}

export interface ModalTitleProps {
  children: React.ReactNode
  className?: string
}

export interface ModalDescriptionProps {
  children: React.ReactNode
  className?: string
}

export interface ModalContentProps {
  children: React.ReactNode
  className?: string
}

export interface ModalActionsProps {
  children: React.ReactNode
  className?: string
}

interface ModalComponentProps {
  onClose?: () => void
  children?: React.ReactNode,
  size?: string
  closeIcon?: ModalCloseIcon
}

interface ModalOverlayProps {
  isVisible: boolean
  children?: React.ReactNode
  placement?: 'center' | 'top'
}

export const Modal: React.FC<ModalProps> & {
  Icon: React.FC<ModalIconProps>
  Title: React.FC<ModalTitleProps>
  Description: React.FC<ModalDescriptionProps>
  Content: React.FC<ModalContentProps>
  Actions: React.FC<ModalActionsProps>
} = (props: ModalProps) => {
  const { isVisible, onClose, children, size, closeIcon, placement } = props
  return (
    <ModalOverlay isVisible={isVisible} placement={placement}>
      <ModalComponent
        onClose={onClose}
        size={size}
        closeIcon={closeIcon}
      >
        {children}
      </ModalComponent>
    </ModalOverlay>
  )
}

export const ModalComponent = (props: ModalComponentProps) => {
  const { onClose, children, size = 'medium', closeIcon } = props
  const showClose = !!closeIcon
  const isCloseVariant = closeIcon === 'close'

  return (
    <div className={classNames({
      [classes.modal]: true,
      [classes[`modal--${size}`]]: true
    })}>
      {showClose && (
        isCloseVariant
          ? (
            <button
              type="button"
              aria-label="Close"
              className={classes.modal__close}
              onClick={() => onClose?.()}
            >
              <FontAwesomeIcon icon={faClose} />
            </button>
          )
          : (
            <FontAwesomeIcon
              icon={faChevronLeft}
              onClick={() => onClose?.()}
            />
          )
      )}
      <div className={classes.modal__body}>
        {children}
      </div>
    </div>
  )
}

const ModalOverlay = (props: ModalOverlayProps) => {
  const { children, isVisible, placement = 'center' } = props
  return (
    <div className={classNames({
      [classes.overlay]: true,
      [classes.visible]: isVisible,
      [classes['overlay--top']]: placement === 'top'
    })}>
      {children}
    </div>
  )
}

const ModalIcon: React.FC<ModalIconProps> = ({ children, className }) => {
  return (
    <header className={classNames(classes.modal__icon, className)}>
      {children}
    </header>
  )
}

const ModalTitle: React.FC<ModalTitleProps> = ({ children, className }) => {
  return (
    <h2 className={classNames(classes.modal__title, className)}>
      {children}
    </h2>
  )
}

const ModalDescription: React.FC<ModalDescriptionProps> = ({ children, className }) => {
  return (
    <p className={classNames(classes.modal__description, className)}>
      {children}
    </p>
  )
}

const ModalContent: React.FC<ModalContentProps> = ({ children, className }) => {
  return (
    <div className={classNames(classes.modal__content, className)}>
      {children}
    </div>
  )
}

const ModalActions: React.FC<ModalActionsProps> = ({ children, className }) => {
  return (
    <div className={classNames(classes.modal__actions, className)}>
      {children}
    </div>
  )
}

Modal.Icon = ModalIcon
Modal.Title = ModalTitle
Modal.Description = ModalDescription
Modal.Content = ModalContent
Modal.Actions = ModalActions